import React from 'react';
import {TscStyles, TscThemeName, TscThemeNames, ControlButton, TscButtonType, Glyphs} from '@techsmith/tsc-cloud-style-guide';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import Constants from '../../constants/Constants';
import {getIsFullscreen} from '../util/FullscreenHelper';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {mediaStore} from '../../context/mediaProvider';
import SmartPlayerConstants from '../../constants/smartPlayerConstants';
import cssConstants from '../../constants/cssConstants';
import {IComment} from '../../model/commentModel';
import {selectedConversationStore} from '../../context/selectedConversationProvider';
import {events} from '@techsmith/techsmith-smartplayer';
import {importMediaInterface} from '../../hooks/dynamicImport';
import MediaTabsToggleButtons from '../content/MediaTabsToggleButtons';
import {useTscTheme} from '../../context/themeProvider';

const minZoomPercentage = 10;
const maxZoomPercentage = 200;
const defaultPercentage = 100;

enum ZoomState {
   none = 'none',
   fill = 'fill',
   fit = 'fit'
}

const ImageScrollContainer = styled.div<{theme: TscThemeNames}>`
   height: 100%;
   width: 100%;
   margin: auto;
   overflow: auto;
   display: grid;
   background-color: ${props => props.theme === TscThemeName.dawn ? SmartPlayerConstants.imageBackgroundColorDawn : SmartPlayerConstants.imageBackgroundColorDusk};
   align-items: center;
`;

export const ImageContainer = styled.div<{width: number; height: number; zoomPercentage: number; isFullscreen: boolean; theme: TscThemeNames}>`
   position: relative;
   margin: 0 auto;

   // we try to not mess with smart-player layers at all, but we have to prevent SP native fullscreen from
   // proc'ing because we control fullscreen and the fullscreen top-layer ourselves in this component
   .smart-player-image-media {
      pointer-events: none;
   }

   user-select: none;

   &.zoom-none {
      width: ${props => props.width * props.zoomPercentage / 100}px;
      height: ${props => props.height * props.zoomPercentage / 100}px;
   }

   &.zoom-fill {
      max-width: ${props => props.width}px;
      max-height: ${props => props.height}px;
   }

   &.zoom-fit {
      width: ${props => props.width * props.zoomPercentage / 100}px;
      height: ${props => props.height * props.zoomPercentage / 100}px;
      max-width: 100%;
      max-height: 100%;
   }

   /* Safari fullscreen fix */
   &:-webkit-full-screen {
      margin-left: 0;
      max-width: 100vw;
      max-height: 100vh;
      height: 100vh;
      width: 100vw;
   }

   .image-container {
      background: ${TscStyles.color.ui.dawn.light};
   }
`;

export const ImageZoomerContainer = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   flex-direction: row;
   gap: 1rem;
   position: absolute;
   top: 1rem;
   right: 1rem;
   user-select: none;
`;

export const ImageZoomer = styled.div<{showZoomControls: boolean}>`
   position: relative;
   margin: auto;
   width: min-content;
   display: flex;
   justify-items: center;
   align-items: center;
   padding: 0 !important;
   border-radius: ${TscStyles.border.radius.md};
   background-color: rgb(46 57 64 / 80%);
   box-shadow: rgba(0, 0, 0, 0.2) 0 1px 3px 0, rgba(255, 255, 255, 0.2) 0 .5px 2px 0 inset, rgba(255, 255, 255, 0.1) 0 0 6px 0 inset;
   backdrop-filter: blur(6px);
   visibility: ${props => props.showZoomControls ? 'visible' : 'hidden'};
   user-select: none;

   & > * {
      margin-right: 0 !important;
   }

   & > *:last-child {
      margin-right: 0 !important;
   }

   & > button {
      color: ${TscStyles.color.ui.dawn.light};
      fill: ${TscStyles.color.ui.dawn.light};
      &:hover {
         box-shadow: none!important;
      }
   }

   .fill-to-width-button, .fit-to-page-button {
      padding: 0 .5rem;
   }
`;

const UISeparator = styled.div`
   margin: 0 .25rem!important;
   border-radius: ${TscStyles.border.radius.xs};
   background: ${TscStyles.color.ui.dusk.lightMedium};
   width: 2px;
   height: 1.25rem;
`;

const ZoomPercentage = styled.div`
   display: inline-flex;
   width: 2.5rem;
   justify-content: center;
   font-size: ${TscStyles.font.size.regular};
   font-weight: ${TscStyles.font.weight.semibold};
   color: ${TscStyles.color.ui.dawn.light};
`;

const switchToFullScreenMode = (elementToFullScreen: React.MutableRefObject<HTMLDivElement>) => {
   if (!elementToFullScreen.current) {
      return;
   }

   const targetElement = elementToFullScreen.current.parentElement;

   if (targetElement.requestFullscreen) {
      void targetElement.requestFullscreen(); // @ts-expect-error polyfill legacy browser support
   } else if (targetElement.webkitRequestFullscreen) { // @ts-expect-error polyfill legacy browser support
      targetElement.webkitRequestFullscreen(); // @ts-expect-error polyfill legacy browser support
   } else if (targetElement.msRequestFullscreen) { // @ts-expect-error polyfill legacy browser support
      targetElement.msRequestFullscreen(); // @ts-expect-error polyfill legacy browser support
   } else if (targetElement.mozRequestFullScreen) { // @ts-expect-error polyfill legacy browser support
      targetElement.mozRequestFullScreen(); // @ts-expect-error polyfill legacy browser support
   } else if (targetElement.webkitEnterFullscreen) { // @ts-expect-error polyfill legacy browser support
      // legacy iOS full screen api
      targetElement.webkitEnterFullscreen();
   }
};

const exitFullscreenMode = (elementToFullScreen: React.MutableRefObject<HTMLDivElement>) => {
   if (!elementToFullScreen.current) {
      return;
   }

   if (window.document.exitFullscreen) {
      void window.document.exitFullscreen(); // @ts-expect-error polyfill legacy browser support
   } else if (window.document.webkitExitFullscreen) { // @ts-expect-error polyfill legacy browser support
      window.document.webkitExitFullscreen(); // @ts-expect-error polyfill legacy browser support
   } else if (window.document.msExitFullscreen) { // @ts-expect-error polyfill legacy browser support
      window.document.msExitFullscreen(); // @ts-expect-error polyfill legacy browser support
   } else if (window.document.mozCancelFullScreen) { // @ts-expect-error polyfill legacy browser support
      window.document.mozCancelFullScreen(); // @ts-expect-error polyfill legacy browser support
   } else if (window.document.fullScreenElement?.webkitExitFullscreen) { // @ts-expect-error polyfill legacy browser support
      // legacy iOS full screen api
      window.document.fullScreenElement.webkitExitFullscreen();
   }
};

// because tscsg gives pass-through refs to the html dom objects for buttons themselves (but does not expose OnMouseDown for the
// button element) we leverage a ref and native dom click handler to prevent focus on-click (but not on-tab), this allows the
// auto-hide to work as desired and not stick as open because a clicked button becomes active
const preventZoomControlButtonMouseClickFocus = (zoomControlButtonRefs: Record<string, React.RefObject<HTMLButtonElement>>) => {
   zoomControlButtonRefs.zoomIn.current && (zoomControlButtonRefs.zoomIn.current.onmousedown = e => {
      e.preventDefault();
   });
   zoomControlButtonRefs.zoomOut.current && (zoomControlButtonRefs.zoomOut.current.onmousedown = e => {
      e.preventDefault();
   });
   zoomControlButtonRefs.fillToPage.current && (zoomControlButtonRefs.fillToPage.current.onmousedown = e => {
      e.preventDefault();
   });
   zoomControlButtonRefs.fitToPage.current && (zoomControlButtonRefs.fitToPage.current.onmousedown = e => {
      e.preventDefault();
   });
   zoomControlButtonRefs.fullScreen.current && (zoomControlButtonRefs.fullScreen.current.onmousedown = e => {
      e.preventDefault();
   });
};

export interface IImageProps {
   children?: React.ReactNode;
}

export interface IStateMappedProps {
   SelectedComment: IComment;
   Rotate: number;
   Width: number;
   Height: number;
}

interface IDimensions {
   width: number;
   height: number;
   isInitialized: boolean;
}

interface IZoom {
   state: ZoomState;
   percentage: number;
}

export const ImageBase: React.FC<IStateMappedProps & IImageProps> = ({children, SelectedComment, Rotate, Width, Height}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();

   const imageScrollContainer = React.useRef<HTMLDivElement>(null); // this will set to null in runtime and will be corrected later, but help setup tests correctly

   const [showFitToPageButton, setShowFitToPageButton] = React.useState(false);
   const [isFullscreen, setIsFullscreen] = React.useState(false);
   const [isTimerActive, setIsTimerActive] = React.useState(false);
   const [isHoveringControls, setIsHoveringControls] = React.useState(false);
   const [isFocused, setIsFocused] = React.useState(false);
   const [isIphoneOrIpod, setIsIphoneOrIpod] = React.useState(false);

   const zoomCtlButtonRefs = {
      zoomIn: React.useRef<HTMLButtonElement>(null),
      zoomOut: React.useRef<HTMLButtonElement>(null),
      fillToPage: React.useRef<HTMLButtonElement>(null),
      fitToPage: React.useRef<HTMLButtonElement>(null),
      fullScreen: React.useRef<HTMLButtonElement>(null)
   };

   const [isHoveringContainer, _setIsHoveringContainer] = React.useState(false);
   const isHoveringContainerRef = React.useRef(isHoveringContainer);
   const setIsHoveringContainer = (value: boolean) => {
      isHoveringContainerRef.current = value;
      _setIsHoveringContainer(value);
   };

   const [zoom, _setZoom] = React.useState<IZoom>({state: ZoomState.fill, percentage: defaultPercentage});
   const zoomRef = React.useRef(zoom);
   const setZoom = (value: IZoom) => {
      zoomRef.current = value;
      _setZoom(value);
   };

   const [dimensions, _setDimensions] = React.useState({width: -1, height: -1, isInitialized: false});
   const dimensionsRef = React.useRef(dimensions);
   const setDimensions = (value: IDimensions) => {
      dimensionsRef.current = value;
      _setDimensions(value);
   };

   const controlsVisibilityTimerId = React.useRef(-1);

   const imageWidth = Rotate === Constants.rotation.ninety || Rotate === Constants.rotation.twoSeventy ? Height : Width;
   const imageHeight = Rotate === Constants.rotation.ninety || Rotate === Constants.rotation.twoSeventy ? Width : Height;
   const zoomIncrement = 10;
   const zoomRoundValue = 10;
   const zoomControlsHideTimeoutMS = 800;
   const zoomControlsLeaveTimeoutMS = 100;

   const preventMouseZoom = (event: WheelEvent) => {
      if (event.ctrlKey && isHoveringContainerRef.current) {
         event.preventDefault();
      }
   };

   // prevents double-click from grabbing focus
   const preventClickToSelect = (event: MouseEvent) => {
      const clickCountToPrevent = 2;
      if (event.detail === clickCountToPrevent && isHoveringContainerRef.current) {
         event.preventDefault();
      }
   };

   const zoomIn = () => {
      const percentage = Math.min(maxZoomPercentage, Math.floor((Math.round(zoomRef.current.percentage) + zoomIncrement) / zoomRoundValue) * zoomRoundValue);
      setZoom({state: ZoomState.none, percentage: percentage});
   };

   const zoomOut = () => {
      const percentage = Math.max(minZoomPercentage, Math.ceil((Math.round(zoomRef.current.percentage) - zoomIncrement) / zoomRoundValue) * zoomRoundValue);
      setZoom({state: ZoomState.none, percentage: percentage});
   };

   const onClickFit = () => {
      const fitToHeight = imageWidth / imageHeight < dimensionsRef.current.width / dimensionsRef.current.height;
      const percentage = fitToHeight ? Math.min(dimensionsRef.current.height / imageHeight * 100, defaultPercentage) : Math.min(dimensionsRef.current.width / imageWidth * 100, defaultPercentage);
      setZoom({state: ZoomState.fit, percentage: percentage});
   };

   const onClickFill = () => {
      const percentage = dimensionsRef.current.width > imageWidth ? 100 : dimensionsRef.current.width / imageWidth * 100;
      setZoom({state: ZoomState.fill, percentage: percentage});
   };

   const onResize = () => {
      if (!imageScrollContainer.current) {
         return;
      }

      const containerWidth = imageScrollContainer.current.offsetWidth;
      const containerHeight = imageScrollContainer.current.offsetHeight;
      const hideFitButton = containerHeight >= imageHeight || containerWidth / (containerHeight + 1) < imageWidth / imageHeight; // Adding 1 to handle pixel rounding

      setDimensions({
         width: containerWidth,
         height: containerHeight,
         isInitialized: true
      });

      setShowFitToPageButton(!hideFitButton);

      if (zoomRef.current.state === ZoomState.fill) {
         onClickFill();
      } else if (zoomRef.current.state === ZoomState.fit) {
         onClickFit();
      }
   };

   const toggleFullScreen = () => {
      if (isFullscreen) {
         exitFullscreenMode(imageScrollContainer);
      } else {
         switchToFullScreenMode(imageScrollContainer);
      }
   };

   const onFullScreenChange = () => {
      setIsFullscreen(!!getIsFullscreen());
      onClickFill();
   };

   const resetControlsTimer = () => {
      setIsTimerActive(true);
      window.clearTimeout(controlsVisibilityTimerId.current);
      const timeout = isHoveringContainerRef.current ? zoomControlsHideTimeoutMS : zoomControlsLeaveTimeoutMS;
      controlsVisibilityTimerId.current = window.setTimeout(() => setIsTimerActive(false), timeout);
   };

   const onFocus = () => {
      setIsFocused(true);
   };

   const onBlur = () => {
      resetControlsTimer();
      setIsFocused(false);
   };

   const stoppedHoveringContainer = () => {
      setIsHoveringContainer(false);
      resetControlsTimer();
   };

   const onWheelHandler = (e: React.WheelEvent<HTMLDivElement>) => {
      if (!e.ctrlKey) {
         return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (e.deltaY < 0) {
         zoomIn();
      } else if (e.deltaY > 0) {
         zoomOut();
      }
   };

   preventZoomControlButtonMouseClickFocus(zoomCtlButtonRefs);

   const scrollImageContainer = (scrollTop: number, scrollLeft: number) => {
      imageScrollContainer.current.scrollTo({
         top: scrollTop,
         left: scrollLeft,
         behavior: 'smooth'
      });
   };

   React.useEffect(() => {
      if (imageScrollContainer.current && SelectedComment?.annotation) {
         try {
            const annotation = JSON.parse(SelectedComment.annotation);
            if (annotation.objects.length > 0) {
               const zoomNormalized = zoom.percentage / 100;
               const scrollPadding = 20;
               const firstAnnotationTopPosition: number = +annotation.objects[0].top * zoomNormalized;
               const firstAnnotationLeftPosition: number = +annotation.objects[0].left * zoomNormalized;
               const firstAnnotationHeight: number = +annotation.objects[0].height * zoomNormalized;
               const firstAnnotationWidth: number = +annotation.objects[0].width * zoomNormalized;
               const scrollContainerHeight: number = imageScrollContainer.current.clientHeight;
               const scrollContainerWidth: number = imageScrollContainer.current.clientWidth;
               const scrollContainerCurrentScrollTop: number = imageScrollContainer.current.scrollTop;
               const scrollContainerCurrentScrollLeft: number = imageScrollContainer.current.scrollLeft;

               let topPosition = scrollContainerCurrentScrollTop;
               let leftPosition = scrollContainerCurrentScrollLeft;

               if (firstAnnotationTopPosition > scrollContainerHeight) {
                  topPosition = firstAnnotationTopPosition + firstAnnotationHeight + (scrollPadding * zoomNormalized) - scrollContainerHeight;
               } else if (firstAnnotationTopPosition < scrollContainerCurrentScrollTop) {
                  topPosition = firstAnnotationTopPosition - (scrollPadding * zoomNormalized);
               }

               if (firstAnnotationLeftPosition > scrollContainerWidth) {
                  leftPosition = firstAnnotationLeftPosition + firstAnnotationWidth + (scrollPadding * zoomNormalized) - scrollContainerWidth;
               } else if (firstAnnotationLeftPosition < scrollContainerCurrentScrollLeft) {
                  leftPosition = firstAnnotationLeftPosition - (scrollPadding * zoomNormalized);
               }

               scrollImageContainer(topPosition, leftPosition);
            }
         } catch (e) {
            // Ignore error
         }
      }
   }, [SelectedComment]);

   React.useEffect(() => {
      document.documentElement.style.setProperty(cssConstants.variables.zoom, `${zoom.percentage / 100}`);
   }, [zoom]);

   React.useEffect(() => {
      const containerObserver = new ResizeObserver(onResize);

      void importMediaInterface().then(mediaInterface => {
         setIsIphoneOrIpod(mediaInterface.isIphoneOrIpod);
         document.addEventListener('fullscreenchange', onFullScreenChange);
         document.addEventListener('mozfullscreenchange', onFullScreenChange);
         document.addEventListener('webkitfullscreenchange', onFullScreenChange);
         document.addEventListener('msfullscreenchange', onFullScreenChange);
         document.addEventListener('wheel', preventMouseZoom, {passive: false});
         document.addEventListener('mousedown', preventClickToSelect);
         mediaInterface.addEventListener(events.External.MediaReady, onResize);
         containerObserver.observe(imageScrollContainer.current);
      });

      return () => {
         void importMediaInterface().then(mediaInterface => {
            document.removeEventListener('fullscreenchange', onFullScreenChange);
            document.removeEventListener('mozfullscreenchange', onFullScreenChange);
            document.removeEventListener('webkitfullscreenchange', onFullScreenChange);
            document.removeEventListener('msfullscreenchange', onFullScreenChange);
            document.removeEventListener('wheel', preventMouseZoom);
            document.removeEventListener('mousedown', preventClickToSelect);
            mediaInterface.removeEventListener(events.External.MediaReady, onResize);
            containerObserver.disconnect();
            window.clearTimeout(controlsVisibilityTimerId.current);
         });
      };
   }, []);

   return (
      <ImageScrollContainer
         ref={imageScrollContainer}
         theme={theme}
         className="image-scroll-container"
         onMouseMove={resetControlsTimer}
         onMouseOver={() => setIsHoveringContainer(true)}
         onMouseOut={stoppedHoveringContainer}
         onWheel={onWheelHandler}
      >
         <ImageContainer
            className={`image-container zoom-${zoom.state}`}
            tabIndex={0}
            width={imageWidth}
            height={imageHeight}
            zoomPercentage={zoom.percentage}
            isFullscreen={isFullscreen}
            theme={theme}
            onMouseDown={e => e.preventDefault()}
            onDoubleClick={e => {
               e.preventDefault();
               e.stopPropagation();
               toggleFullScreen();
            }}
            onFocus={onFocus}
            onBlur={onBlur}
            data-test-id="image-container"
         >
            {children}
         </ImageContainer>
         <ImageZoomerContainer>
            <ImageZoomer
               className="image-zoom"
               onMouseOver={() => setIsHoveringControls(true)}
               onMouseOut={() => setIsHoveringControls(false)}
               onFocus={onFocus}
               onBlur={onBlur}
               showZoomControls={isTimerActive || isHoveringControls || isFocused}
            >
               <ControlButton
                  buttonType={TscButtonType.tertiary}
                  title={t('image.zoomOutButtonTitle')}
                  themeName={TscThemeName.dusk}
                  glyph={<Glyphs.CircleRemove16x16 />}
                  testId="zoom-out-btn"
                  onClick={zoomOut}
                  ref={zoomCtlButtonRefs.zoomOut}
               />
               <ZoomPercentage className="zoom-value">
                  {Math.round(zoom.percentage)}
                  %
               </ZoomPercentage>
               <ControlButton
                  buttonType={TscButtonType.tertiary}
                  title={t('image.zoomInButtonTitle')}
                  themeName={TscThemeName.dusk}
                  glyph={<Glyphs.CircleAdd16x16 />}
                  testId="zoom-in-btn"
                  onClick={zoomIn}
                  ref={zoomCtlButtonRefs.zoomIn}
               />
               <ControlButton
                  buttonType={TscButtonType.tertiary}
                  title={t('image.fillToWidth')}
                  className="fill-to-width-button"
                  themeName={TscThemeName.dusk}
                  label={t('image.fillToWidth')}
                  aria={{'aria-label': t('image.a11yFillToWidth')}}
                  testId="fill-btn"
                  onClick={onClickFill}
                  ref={zoomCtlButtonRefs.fillToPage}
               />
               {showFitToPageButton && <UISeparator />}
               {showFitToPageButton && (
                  <ControlButton
                     buttonType={TscButtonType.tertiary}
                     title={t('image.fitToPage')}
                     className="fit-to-page-button"
                     themeName={TscThemeName.dusk}
                     label={t('image.fitToPage')}
                     aria={{'aria-label': t('image.a11yFitToPage')}}
                     testId="fit-to-page-btn"
                     onClick={onClickFit}
                     ref={zoomCtlButtonRefs.fitToPage}
                  />
               )}
               {!isIphoneOrIpod && (
                  <ControlButton
                     buttonType={TscButtonType.tertiary}
                     title={isFullscreen ? t('image.exitFullscreenView') : t('image.enterFullscreenView')}
                     themeName={TscThemeName.dusk}
                     glyph={isFullscreen ? <Glyphs.FullscreenExit16x16 /> : <Glyphs.FullscreenEnter16x16 />}
                     testId="fullscreen-image-btn"
                     onClick={toggleFullScreen}
                     ref={zoomCtlButtonRefs.fullScreen}
                  />
               )}
            </ImageZoomer>
            <MediaTabsToggleButtons hidden={isFullscreen} />
         </ImageZoomerContainer>
      </ImageScrollContainer>
   );
};

const mapStatesToProps = (
   {children}: IImageProps,
   {SelectedComment}: Partial<IStateMappedProps>,
   {Rotate, Width, Height}: Partial<IStateMappedProps>
): IStateMappedProps & IImageProps => ({children, SelectedComment, Rotate, Width, Height});

export default withMemoizedContexts(mapStatesToProps, selectedConversationStore, mediaStore)(ImageBase);
